import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { fetchIntegrationConfig } from "../redux/thunks/fetchIntegrationConfig";
import { useAppDispatch } from "../redux/hooks";

type HhaExchangeItem = {
  ID: string;
  HomePhone?: string;
  Phone2?: string;
  Phone3?: string;
  RecordType?: "Caregiver" | "Patient";
};

type Integration = {
  name: string;
  config?: {
    ent_id?: string;
  };
};

type IntegrationDataItem = {
  type: string;
  onClick: () => void;
  data: HhaExchangeItem;
};

export const useGetIntegrationData = () => {
  const dispatch = useAppDispatch();

  const integrations = useSelector((state: RootState) => state.integrations.items ?? []);
  const hhaexchange = useSelector((state: RootState) => state.hhaexchange.items ?? []);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setIsFetching(true);
    const timeout = setTimeout(() => setIsFetching(false), 200);
    return () => clearTimeout(timeout);
  }, [integrations, hhaexchange]);

  const getIntegrationsData = (number: string): IntegrationDataItem[] | undefined => {
    if (!number || integrations.length === 0 || hhaexchange.length === 0) return;

    return integrations.flatMap((integration) => {
      if (integration.name !== "hhaexchange" || !integration.config?.ent_id) return [];

      const match = hhaexchange.find((item) =>
        [item.HomePhone, item.Phone2, item.Phone3].includes(number)
      );

      if (!match) return [];

      const ent_id = integration.config.ent_id;
      const url =
        match.RecordType === "Caregiver"
          ? `https://app.hhaexchange.com/${ent_id}/Aide/Aide.aspx?AideId=${match.ID}`
          : `https://app.hhaexchange.com/${ent_id}/Patient/InternalPatientInfo.aspx?PatientId=${match.ID}`;

      return [
        {
          type: "hhaexchange",
          onClick: () => window.open(url, "_blank"),
          data: match,
        },
      ];
    });
  };

  const getIntegrationConfig = () => {
    integrations.forEach((integration) => {
      if (integration.name === "hhaexchange" && !integration.config?.ent_id) {
        dispatch(fetchIntegrationConfig(integration.name));
      }
    });
  };

  return {
    getIntegrationsData,
    getIntegrationConfig,
    isFetching,
  };
};
